import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";
import { mapSectionWithExtendedOptions, } from "../../components/ConsentSection";
import { Controller, useFormContext } from "../../components/Form";
import { OneTrustErrorBanner } from "../../components/OneTrustErrorBanner/OneTrustErrorBanner";
import { OptionalConsents } from "../../components/OptionalConsents/OptionalConsents";
import { useConfig } from "../../providers/useConfig";
import { useTranslations } from "../../providers/useTranslations";
import { replaceOneTrustPlaceholdersWithLinks } from "../../utils/linkReplacer/linkReplacer";
import { groupPurposesBySection } from "../../utils/purposes";
import { Checkbox } from "../Checkbox/Checkbox";
import styles from "./RegistrationPolicyCheckFields.module.scss";
const { sanitize } = dompurify;
export const RegistrationPolicyCheckFields = ({ fieldName = (name) => name, policyFields = [], disabled, isError, oneTrustErrorLinkText, oneTrustErrorRedirectUrl, oneTrustErrorMessage, isOldStyle = true, consentOptionsListType, consentsGroupingType, acceptAllButtonMode, }) => {
    const { __ } = useTranslations();
    const { setValue, control } = useFormContext();
    const config = useConfig();
    const { mandatoryFields, optionalConsents, optionalConsentsLayout, } = useMemo(() => {
        const mandatoryFields = policyFields.filter(({ mandatory }) => mandatory);
        const nonMandatoryFields = policyFields.filter(({ mandatory }) => !mandatory);
        if (consentOptionsListType === "extended") {
            const grouped = groupPurposesBySection(nonMandatoryFields);
            const extended = mapSectionWithExtendedOptions({
                sections: grouped,
                resetDefaultValues: true,
            });
            return { mandatoryFields, optionalConsents: extended, optionalConsentsLayout: "extended" };
        }
        if (consentsGroupingType === "section") {
            const grouped = groupPurposesBySection(nonMandatoryFields);
            return { mandatoryFields, optionalConsents: grouped, optionalConsentsLayout: "section" };
        }
        return { mandatoryFields, optionalConsents: nonMandatoryFields, optionalConsentsLayout: "default" };
    }, [policyFields, consentOptionsListType, consentsGroupingType]);
    const handleAcceptAllClick = () => {
        if (!optionalConsents.length)
            return;
        // NOTE: For now, this does not fully support "extended" options like "true--false" or "true--true".
        // It was rejected to implement for now, so we're setting just "true" even for multi-option consents.
        const purposes = optionalConsentsLayout === "default"
            ? optionalConsents
            : optionalConsents.flatMap((section) => section.purposes);
        purposes.forEach(({ id }) => {
            setValue(`purposes.${id}`, "true");
        });
    };
    if (isError) {
        return (_jsx(OneTrustErrorBanner, { title: __("auth.registration.consents.error.title"), linkText: oneTrustErrorLinkText, redirectUrl: oneTrustErrorRedirectUrl, errorMessage: oneTrustErrorMessage || "" }));
    }
    return (_jsxs("div", { className: classNames(styles.wrapper, {
            [styles.optionalConsentsWrapper]: isOldStyle,
        }), children: [_jsx("div", { className: styles.checkboxGroup, children: mandatoryFields.map((field) => (_jsx(Controller, { control: control, name: fieldName(field.id), render: ({ field: hookFormField, fieldState }) => (_jsx(Checkbox, { ...hookFormField, defaultChecked: true, required: field.mandatory, onChange: (newVal) => hookFormField.onChange(typeof newVal === "object" ? newVal.checked : newVal), value: { checked: field.default || hookFormField.value }, dataTestId: `policyCheckbox-${field.id}`, id: field.id, disabled: disabled, isError: !!fieldState.error, errorText: fieldState.error?.message, labelHtml: sanitize(config.ONE_TRUST_DYNAMIC_LINKS
                            ? replaceOneTrustPlaceholdersWithLinks(field.description, config.ONE_TRUST_DYNAMIC_LINKS)
                            : field.description, { ADD_ATTR: ["target"] }) })), rules: { required: { value: field.mandatory, message: __("form_validation.field_required") } }, defaultValue: field.default }, field.id))) }), _jsx(OptionalConsents, { disabled: disabled, purposes: optionalConsents, layout: optionalConsentsLayout, acceptAllButtonMode: acceptAllButtonMode, onAcceptAllClick: handleAcceptAllClick })] }));
};
//# sourceMappingURL=RegistrationPolicyCheckFields.js.map